/**
 * De volledige beurtcyclus als één samenhangend geheel.
 *
 * `moves.ts` bevat de losse zetten; hier worden ze aan elkaar geknoopt tot een potje
 * dat van begin tot eind gespeeld kan worden. De regels over wie wat wanneer mag,
 * staan in `isLegalAction`; het uitvoeren in `perform`.
 */
import {
  BASIC_CARDS,
  LANDSCAPE_RESOURCE,
  LIMITS,
  type Resource,
  type Seat,
} from '@catan/contracts';
import {
  canAfford,
  canPlaceExpansion,
  checkPayment,
  groeirichting,
  legLandschappen,
  pay,
  verkennerInHand,
  voorraadBouwwerken,
  roadSpots,
  settlementSpots,
  victoryPoints,
  type Payment,
} from './building.js';
import { distributeProduction } from './opbrengst.js';
import { rollDice } from './dice.js';
import type { BuildingKind, GameState, PlayerState, Side, StartLandschap } from './state.js';
import { zelfdeBundel } from './state.js';
import { addResource, removeResource } from './events.js';
import {
  handelaarKlaar,
  isLegalChoice,
  resolveChoice,
  resolveEvent,
  type CardStats,
} from './turn.js';
import { updateTokens } from './tokens.js';
import {
  actionCardName,
  actionCardsUnlocked,
  destructibleBuildings,
  isCounterOnly,
  knightsOf,
  needsThreePoints,
} from './actioncards.js';
import {
  beslechtAanval,
  heeftPlaatsbareBuit,
  playActionCard,
  type CardTarget,
} from './playcard.js';
import { bankRate, canFulfil, canOffer, isSensibleOffer, type TradeAbilities } from './trading.js';

const cardById = new Map(BASIC_CARDS.map((c) => [c.id, c]));

/** Kaarteigenschappen zoals de regels ze nodig hebben, uit de kaartdata. */
export const CARD_STATS: CardStats = {
  commercePoints: (id) => cardById.get(id)?.commercePoints ?? 0,
  tournamentPoints: (id) => cardById.get(id)?.tournamentPoints ?? 0,
  strength: (id) => cardById.get(id)?.strength ?? 0,
  isWarehouse: (id) => cardById.get(id)?.name === 'Pakhuis',
};

const BUILDING_POINTS = (kind: BuildingKind): number => (kind === 'dorp' ? 1 : 2);

/**
 * Gebouwen die je hand vergroten (kennisbank 02 §2.3).
 * Universiteit en Theater komen uit de themasets en staan er alvast in.
 */
const HAND_BONUS: Readonly<Record<string, number>> = {
  Bibliotheek: 1,
  Klooster: 1,
  Universiteit: 1,
  Theater: 2,
};

/**
 * Wat een speler aan handelsvoordeel heeft.
 *
 * Een handelsvloot maakt de koers voor zijn eigen grondstof 2:1; de munterij doet dat
 * voor goud zelfs 1:1 ("1 goud → 1 grondstof", kennisbank 02 §2.2).
 */
export function tradeAbilitiesOf(player: PlayerState): TradeAbilities {
  const fleets = new Set<Resource>();
  const direct = new Set<Resource>();
  for (const cardId of player.principality.buildings.flatMap((b) => [
    ...b.expansions.boven,
    ...b.expansions.onder,
  ])) {
    const naam = cardById.get(cardId)?.name ?? '';
    const vloot = FLEET_RESOURCE[naam];
    if (vloot !== undefined) fleets.add(vloot);
    if (naam === 'Munterij') direct.add('goud');
  }
  return { fleets, direct };
}

/** Welke vloot hoort bij welke grondstof. */
const FLEET_RESOURCE: Readonly<Record<string, Resource>> = {
  Wolvloot: 'wol',
  Ertsvloot: 'erts',
  Houtvloot: 'hout',
  Goudvloot: 'goud',
  Graanvloot: 'graan',
  Steenvloot: 'klei',
};

/** De handlimiet van een speler: basis 3, plus wat hij gebouwd heeft. */
export function handLimitOf(player: PlayerState): number {
  const bonus = player.principality.buildings
    .flatMap((b) => [...b.expansions.boven, ...b.expansions.onder])
    .reduce((sum, id) => sum + (HAND_BONUS[cardById.get(id)?.name ?? ''] ?? 0), 0);
  return LIMITS.baseHandLimit + bonus;
}
const CARD_POINTS = (id: number): number => cardById.get(id)?.victoryPoints ?? 0;

/**
 * De worp uit een verslagregel, als er in deze zetten gegooid is.
 *
 * Hoort bij de regel `"X gooit 3 en gebeurteniskaart"` die `voerUit` schrijft — producent
 * en lezer op één afspraak, met een toets die ze vastpint (zelfde patroon als
 * `aanvalsworpRegel`/`aanvalsworpUit`). Het afspeelpad leest hem terug omdat de levende
 * weergave de worp al kwijt is zodra de beurt in dezelfde partij ook eindigde — en dan
 * werd de steen stilzwijgend overgeslagen terwijl de bouwflitsen al draaiden.
 */
const GEBEURTENIS_VLAKKEN = new Set([
  'goed-jaar',
  'handelsvoordeel',
  'roofoverval',
  'riddertoernooi',
  'gebeurteniskaart',
]);
export function gooiUit(
  regels: readonly string[],
): { production: 1 | 2 | 3 | 4 | 5 | 6; event: string } | undefined {
  for (const regel of regels) {
    const treffer = / gooit ([1-6]) en ([a-z-]+)$/.exec(regel);
    if (treffer !== null && GEBEURTENIS_VLAKKEN.has(treffer[2] ?? '')) {
      return { production: Number(treffer[1]) as 1 | 2 | 3 | 4 | 5 | 6, event: treffer[2] as string };
    }
  }
  return undefined;
}

/** Alles wat een speler kan doen. */
export type Action =
  | { readonly type: 'gooi' }
  | { readonly type: 'kies'; readonly column: number; readonly side: Side }
  | { readonly type: 'bouw-straat'; readonly column: number; readonly payment: Payment }
  | { readonly type: 'bouw-dorp'; readonly column: number; readonly payment: Payment }
  | { readonly type: 'bouw-stad'; readonly column: number; readonly payment: Payment }
  | { readonly type: 'pak-kaart'; readonly stack: number }
  /**
   * Een uitbreidingskaart uit je hand in je vorstendom bouwen.
   *
   * Groene kaarten mogen bij een dorp én een stad, rode alleen bij een stad. Een dorp
   * heeft één plaats per zijde, een stad twee (handboek p. 14).
   */
  | {
      readonly type: 'bouw-uitbreiding';
      readonly cardId: number;
      readonly column: number;
      readonly side: Side;
      readonly payment: Payment;
    }
  | {
      readonly type: 'speel-actiekaart';
      readonly cardId: number;
      readonly target?: CardTarget;
    }
  /**
   * Ruilen met de bank: je wijst de landschappen aan waar je grondstoffen vandaan haalt
   * en het landschap waar de nieuwe grondstof heen gaat.
   */
  | {
      readonly type: 'ruil-bank';
      readonly give: Payment;
      readonly receive: { readonly column: number; readonly side: Side };
    }
  /** Een ruilaanbod doen aan je tegenstander. */
  | {
      readonly type: 'ruil-aanbod';
      readonly give: Readonly<Partial<Record<Resource, number>>>;
      readonly want: Readonly<Partial<Record<Resource, number>>>;
    }
  /** Het aanbod van je tegenstander aannemen of afwijzen. */
  | { readonly type: 'ruil-antwoord'; readonly accepteren: boolean }
  /**
   * Je tegenkaart tegen een aanval wel of niet inzetten.
   *
   * Het origineel vraagt dat met een banner; de kaart gaat er niet vanzelf uit. Zet je
   * hem in, dan slaagt de aanval alleen nog bij een 1 of 2 — maar je bent de kaart kwijt.
   */
  | { readonly type: 'tegenkaart'; readonly inzetten: boolean }
  /**
   * De Verkenner wel of niet inzetten bij het dorp dat je zojuist stichtte.
   *
   * Zeg je nee, dan komen de twee bovenste landschappen van de stapel. Zeg je ja, dan
   * raak je de kaart kwijt en zoek je ze zelf uit (handboek p. 46).
   */
  | { readonly type: 'verkenner'; readonly inzetten: boolean }
  /** In de aanvulfase een handkaart onder een stapel schuiven en er een nieuwe pakken. */
  | { readonly type: 'ruil-kaart'; readonly handIndex: number; readonly stack: number }
  /**
   * Een handkaart onder een stapel schuiven, zónder er meteen een terug te krijgen.
   *
   * Het handboek is strenger dan de kaarttekst: "Uitsluitend wanneer een speler in zijn
   * beurt zijn hand niet mag aanvullen, mag hij één ontwikkelingskaart ruilen. Hij schuift
   * één van zijn kaarten onder een ontwikkelingsstapel naar keuze, waarná hij volgens a) of
   * b) een nieuwe kaart mag trekken of uitzoeken" (p. 10).
   *
   * Twee stappen dus, en dat is precies het verschil met `ruil-kaart`: die ruilt blind — je
   * krijgt de bovenste van de stapel die je aanwijst, zonder keuze en zonder de mogelijkheid
   * om ervoor te betalen en te zoeken. In het echte potje kwam dat 72 keer voor: samen met
   * `beurt-beeindigen` de meest gespeelde zet. `ruil-kaart` blijft bestaan voor de AI.
   */
  | { readonly type: 'ruil-af'; readonly handIndex: number; readonly stack: number }
  /**
   * De Handelaar houdt op na één grondstof.
   *
   * "Neem 1 **of 2** grondstoffen naar jouw keuze af" (p. 12 §9): na de eerste mag je
   * stoppen. Dit is die stop; hij is alleen wettig terwijl de handelaarsvraag openstaat
   * en er al precies één gepakt is. De teruggave volgt gewoon.
   */
  | { readonly type: 'kies-klaar' }
  /**
   * Twee grondstoffen betalen om een stapel door te kijken en er vrij uit te kiezen.
   *
   * Het alternatief voor gratis de bovenste pakken (handboek p. 16). Het origineel vraagt
   * dat met "Bovenste kaart nemen?" of "Nieuwe kaart kopen?".
   */
  | { readonly type: 'koop-kaart'; readonly stack: number; readonly payment: Payment }
  /**
   * Twee van je eigen landschappen van plek wisselen, tijdens de opzet.
   *
   * Zo verdeel je je startlandschappen zelf, zoals het origineel aan het begin vraagt.
   * Voor de speler voelt het als plaatsen: je pakt er een en zet hem waar je wilt; wat
   * daar lag gaat terug. Ruilen in plaats van uit een voorraad plaatsen houdt het
   * vorstendom op elk moment compleet — er is nooit een half gevuld bord.
   */
  | {
      readonly type: 'wissel-landschap';
      readonly a: { readonly column: number; readonly side: Side };
      readonly b: { readonly column: number; readonly side: Side };
    }
  /**
   * Een landschap uit je voorraad op een lege plek leggen, tijdens de opzet.
   *
   * `poolIndex` wijst het landschap aan, `column`/`side` de plek. De zes plekken staan
   * vast (handboek p. 10): kolommen 0, 2 en 4, boven en onder de dorpenrij.
   */
  | {
      readonly type: 'plaats-landschap';
      readonly poolIndex: number;
      readonly column: number;
      readonly side: Side;
    }
  /** Bij de opzet: de uitbouwstapel kiezen waaruit je je drie startkaarten pakt. */
  | { readonly type: 'kies-startstapel'; readonly stack: number }
  /** De opzet afsluiten en beginnen. */
  | { readonly type: 'opzet-klaar' }
  | { readonly type: 'beurt-beeindigen' };

/**
 * De zes plekken waar een startlandschap kan liggen.
 *
 * Diagonaal rond de twee dorpen: kolommen 0, 2 en 4, boven en onder de dorpenrij
 * (handboek p. 10).
 */
const STARTPLEKKEN: readonly { column: number; side: Side }[] = [
  { column: 0, side: 'boven' },
  { column: 2, side: 'boven' },
  { column: 4, side: 'boven' },
  { column: 0, side: 'onder' },
  { column: 2, side: 'onder' },
  { column: 4, side: 'onder' },
];

/**
 * Wat het jou kost om een stapel door te kijken.
 *
 * Standaard twee grondstoffen. Een **raadhuis** maakt er één van — "voor het uitkiezen
 * van een kaart uit een kaartenstapel betaal je 1 grondstof minder (een tweede raadhuis
 * verlaagt het aantal benodigde grondstoffen niet verder)".
 */
export function koopPrijsVoor(player: PlayerState): number {
  const heeftRaadhuis = player.principality.buildings.some((b) =>
    [...b.expansions.boven, ...b.expansions.onder].some(
      (id) => cardById.get(id)?.name === 'Raadhuis',
    ),
  );
  return heeftRaadhuis ? KOOP_PRIJS - 1 : KOOP_PRIJS;
}

/**
 * Hoeveel ruilaanbiedingen je per beurt mag doen.
 *
 * Het origineel laat de computer er tot vijf doen (`90-speldata/ai/1/23_aktiverHandel.ki`,
 * rijen 26-30). Zonder die grens herhaalt hij hetzelfde voorstel zodra je het afwijst, en
 * kom je de beurt niet uit.
 */
export const MAX_AANBIEDINGEN = 5;

/** Hoeveel kaarten je aan het begin uit je gekozen stapel pakt (handboek p. 11). */
export const START_KAARTEN = 3;

/** Wat het kost om een stapel door te kijken (handboek p. 16). */
export const KOOP_PRIJS = 2;

export type Verdict = { readonly ok: true } | { readonly ok: false; readonly reason: string };
const JA: Verdict = { ok: true };
const nee = (reason: string): Verdict => ({ ok: false, reason });

function costOf(kind: 'straat' | 'dorp' | 'stad') {
  const naam = kind === 'straat' ? 'Straat' : kind === 'dorp' ? 'Dorp' : 'Stad';
  const card = BASIC_CARDS.find((c) => c.name === naam);
  if (card === undefined) throw new Error(`kaart ${naam} ontbreekt in de kaartdata`);
  return card.cost;
}

export function isLegalAction(state: GameState, seat: Seat, action: Action): Verdict {
  if (state.winner !== undefined) return nee('het potje is afgelopen');

  // Een openstaande keuze gaat voor alles: zolang die er is, kan er niets anders.
  if (state.pending !== undefined) {
    if (state.pending.kind === 'kies-tegenkaart') {
      if (action.type !== 'tegenkaart') return nee('antwoord eerst over je tegenkaart');
      return state.pending.seat === seat ? JA : nee('deze keuze is niet aan jou');
    }
    if (state.pending.kind === 'verkenner-vraag') {
      if (action.type !== 'verkenner') return nee('antwoord eerst over de Verkenner');
      return state.pending.seat === seat ? JA : nee('deze keuze is niet aan jou');
    }
    // "Neem 1 of 2 grondstoffen af" — na de eerste mag de Handelaar-speler stoppen.
    if (state.pending.kind === 'handelaar' && action.type === 'kies-klaar') {
      if (state.pending.seat !== seat) return nee('deze keuze is niet aan jou');
      return state.pending.resterend === 1 ? JA : nee('pak eerst een grondstof');
    }
    if (action.type !== 'kies') return nee('er staat eerst een keuze open');
    if (state.pending.seat !== seat) return nee('deze keuze is niet aan jou');
    const oordeel = isLegalChoice(state, seat, { column: action.column, side: action.side });
    return oordeel.ok ? JA : nee(oordeel.reden ?? 'die keuze kan niet');
  }
  if (action.type === 'kies') return nee('er staat geen keuze open');
  if (action.type === 'kies-klaar') return nee('er is nu niets af te ronden');
  if (action.type === 'tegenkaart') return nee('er komt geen aanval op je af');
  if (action.type === 'verkenner') return nee('je hebt net geen dorp gebouwd');
  // Op een ruilaanbod antwoordt juist de speler die níét aan de beurt is. En tijdens de
  // opzet richt elke speler zijn eigen vorstendom in — daar is geen beurt in.
  const buitenDeBeurt =
    action.type === 'ruil-antwoord' ||
    action.type === 'kies-startstapel' ||
    action.type === 'plaats-landschap' ||
    action.type === 'opzet-klaar';
  if (!buitenDeBeurt && seat !== state.turn) {
    return nee('je bent niet aan de beurt');
  }

  const player = state.players[seat];

  switch (action.type) {
    case 'gooi':
      return state.phase === 'dobbelen' ? JA : nee(`dobbelen kan niet in "${state.phase}"`);

    case 'bouw-straat': {
      if (state.phase !== 'hoofdfase') return nee('bouwen kan alleen in de hoofdfase');
      if (voorraadBouwwerken(state.players[0].principality, state.players[1].principality, 'straat') === 0) {
        return nee('de straten zijn op');
      }
      if (!roadSpots(player.principality).includes(action.column)) {
        return nee(`daar kan geen straat (kolom ${action.column})`);
      }
      return checkPaymentVerdict(player, 'straat', action.payment);
    }

    case 'bouw-dorp': {
      if (state.phase !== 'hoofdfase') return nee('bouwen kan alleen in de hoofdfase');
      if (voorraadBouwwerken(state.players[0].principality, state.players[1].principality, 'dorp') === 0) {
        return nee('de dorpen zijn op — bouw een stad, dan komt er weer een in de voorraad');
      }
      if (!settlementSpots(player.principality).includes(action.column)) {
        return nee(`daar kan geen dorp (kolom ${action.column})`);
      }
      return checkPaymentVerdict(player, 'dorp', action.payment);
    }

    case 'bouw-stad': {
      if (state.phase !== 'hoofdfase') return nee('bouwen kan alleen in de hoofdfase');
      if (voorraadBouwwerken(state.players[0].principality, state.players[1].principality, 'stad') === 0) {
        return nee('je steden zijn op');
      }
      const doel = player.principality.buildings.find((b) => b.column === action.column);
      if (doel === undefined) return nee('daar staat geen bouwwerk');
      if (doel.kind !== 'dorp') return nee('daar staat al een stad');
      return checkPaymentVerdict(player, 'stad', action.payment);
    }

    case 'bouw-uitbreiding': {
      if (state.phase !== 'hoofdfase') return nee('bouwen kan alleen in de hoofdfase');
      if (!player.hand.includes(action.cardId)) return nee('die kaart zit niet in je hand');
      const kaart = cardById.get(action.cardId);
      if (kaart === undefined || kaart.kind !== 'uitbouw') {
        return nee('die kaart kun je niet in je vorstendom bouwen');
      }
      const bouwwerk = player.principality.buildings.find((b) => b.column === action.column);
      if (bouwwerk === undefined) return nee('daar staat geen bouwwerk');
      if (kaart.placement === 'alleen-stad' && bouwwerk.kind !== 'stad') {
        return nee(`de ${kaart.name} kan alleen bij een stad`);
      }
      const plaats = canPlaceExpansion(bouwwerk, action.side);
      if (!plaats.ok) return nee(plaats.reason);
      if (!canAfford(player, kaart.cost)) return nee(`je hebt niet genoeg voor de ${kaart.name}`);
      const betaling = checkPayment(player, kaart.cost, action.payment);
      return betaling.ok ? JA : nee(betaling.reason);
    }

    case 'pak-kaart': {
      if (state.phase !== 'aanvullen') return nee('kaarten pakken kan alleen aan het eind van je beurt');
      if (player.hand.length >= handLimitOf(player)) return nee('je hand is vol');
      const stapel = state.stacks[action.stack];
      if (stapel === undefined) return nee(`stapel ${action.stack} bestaat niet`);
      if (stapel.length === 0) return nee('die stapel is leeg');
      return JA;
    }

    case 'wissel-landschap': {
      if (state.phase !== 'opzet') return nee('landschappen verdelen kan alleen bij de opzet');
      for (const plek of [action.a, action.b]) {
        const tegel = player.principality.landscapes.find(
          (t) => t.column === plek.column && t.side === plek.side,
        );
        if (tegel === undefined) return nee('dat landschap is niet van jou');
      }
      if (action.a.column === action.b.column && action.a.side === action.b.side) {
        return nee('dat is dezelfde plek');
      }
      return JA;
    }

    case 'plaats-landschap': {
      if (state.phase !== 'opzet') return nee('landschappen leggen kan alleen bij de opzet');
      const voorraad = state.setupPool[seat];
      if (voorraad[action.poolIndex] === undefined) return nee('dat landschap heb je niet');
      if (!STARTPLEKKEN.some((p) => p.column === action.column && p.side === action.side)) {
        return nee('daar hoort geen landschap');
      }
      const bezet = player.principality.landscapes.some(
        (t) => t.column === action.column && t.side === action.side,
      );
      return bezet ? nee('daar ligt al een landschap') : JA;
    }

    case 'kies-startstapel': {
      if (state.phase !== 'opzet') return nee('startkaarten kies je bij de opzet');
      if (player.hand.length > 0) return nee('je hebt je startkaarten al');
      const stapel = state.stacks[action.stack];
      if (stapel === undefined || stapel.length < START_KAARTEN) {
        return nee('die stapel heeft te weinig kaarten');
      }
      return JA;
    }

    case 'opzet-klaar': {
      if (state.phase !== 'opzet') return nee('de opzet is al voorbij');
      // Het spel begint pas als **beide** spelers klaar zijn. Anders sluit degene die
      // toevallig als eerste klaar is de opzet af terwijl de ander nog moet kiezen — en
      // die begint dan met een lege hand.
      for (const wie of [0, 1] as const) {
        if (state.setupPool[wie].length > 0) {
          return wie === seat
            ? nee(`je moet nog ${state.setupPool[wie].length} landschappen leggen`)
            : nee('je tegenstander is nog bezig');
        }
        if (state.players[wie].hand.length < START_KAARTEN) {
          return wie === seat
            ? nee(`kies eerst je ${START_KAARTEN} startkaarten`)
            : nee('je tegenstander is nog bezig');
        }
      }
      return JA;
    }

    case 'koop-kaart': {
      if (state.phase !== 'aanvullen') return nee('een kaart kopen kan aan het eind van je beurt');
      if (player.hand.length >= handLimitOf(player)) return nee('je hand is vol');
      const stapel = state.stacks[action.stack];
      if (stapel === undefined || stapel.length === 0) return nee('die stapel is leeg');
      const prijs = koopPrijsVoor(player);
      if (action.payment.length !== prijs) {
        return nee(`een kaart kopen kost ${prijs} grondstoffen`);
      }
      // Je mag met alles betalen, als je het maar hebt liggen.
      for (const ref of action.payment) {
        const tegel = player.principality.landscapes.find(
          (t) => t.column === ref.column && t.side === ref.side,
        );
        if (tegel === undefined) return nee('dat landschap is niet van jou');
      }
      const geteld = new Map<string, number>();
      for (const ref of action.payment) {
        const sleutel = `${ref.column}:${ref.side}`;
        geteld.set(sleutel, (geteld.get(sleutel) ?? 0) + 1);
      }
      for (const [sleutel, aantal] of geteld) {
        const [kolom, zijde] = sleutel.split(':');
        const tegel = player.principality.landscapes.find(
          (t) => t.column === Number(kolom) && t.side === zijde,
        );
        if ((tegel?.resources ?? 0) < aantal) return nee('daar liggen niet genoeg grondstoffen');
      }
      return JA;
    }

    case 'speel-actiekaart': {
      if (state.phase !== 'hoofdfase' && state.phase !== 'dobbelen') {
        return nee('actiekaarten speel je tijdens je beurt');
      }
      if (!player.hand.includes(action.cardId)) return nee('die kaart zit niet in je hand');
      const naam = actionCardName(action.cardId);
      if (naam === undefined) return nee('dat is geen actiekaart');
      if (isCounterOnly(naam)) return nee(`de ${naam} werkt alleen als verweer`);
      // "Deze kaart wordt meteen na de bouw van een nieuw dorp gespeeld" (handboek p. 46).
      // Het spel vraagt er dan zelf om; uit je hand spelen doe je hem niet.
      if (naam === 'Verkenner') return nee('de Verkenner zet je in bij het bouwen van een dorp');
      if (naam === 'Alchimist' && state.phase !== 'dobbelen') {
        return nee('de Alchimist speel je vóór het gooien');
      }
      if (naam !== 'Alchimist' && state.phase !== 'hoofdfase') {
        return nee('deze kaart speel je in de hoofdfase');
      }
      if (needsThreePoints(naam)) {
        const punten: [number, number] = [pointsOf(state, 0), pointsOf(state, 1)];
        if (!actionCardsUnlocked(punten)) {
          return nee('actiekaarten kunnen pas als beide spelers drie punten hebben');
        }
      }
      // "Bij sommige actiekaarten moeten er voorwaarden vervuld zijn" (handboek p. 19).
      // Een aanval zonder doelwit is er zo een: de kaart zou alleen verdwijnen. Het
      // origineel laat je hem dan niet kiezen, en dat hoort in de kern te zitten — niet
      // in de tegenstander, want dan mag een mens wat een computer niet mag.
      const ander = state.players[seat === 0 ? 1 : 0];
      if (naam === 'Vuurduivel' && destructibleBuildings(ander).length === 0) {
        return nee('er staat geen gebouw om af te branden');
      }
      if (naam === 'Zwarte ridder' && knightsOf(ander).length === 0) {
        return nee('er staat geen ridder om uit te schakelen');
      }
      if (naam === 'Rooftocht' && !ander.principality.landscapes.some((t) => t.resources > 0)) {
        return nee('er ligt niets om te roven');
      }
      // "Verwijder 2 van je grondstoffen" — zonder twee valt er niets te ruilen. Het
      // boekje ruilt gerust twee van dezelfde tegel ("je draait je bos van 3 naar 1
      // terug"), dus we tellen het totaal en niet het aantal gevulde tegels.
      if (naam === 'Karavaan') {
        const totaal = player.principality.landscapes.reduce((som, t) => som + t.resources, 0);
        if (totaal < 2) return nee('je hebt geen twee grondstoffen om te ruilen');
      }
      // De Handelaar moet iets kunnen halen dat hij ook kwijt kan (p. 12 §9); anders is
      // de kaart alleen maar weg.
      if (naam === 'Handelaar' && !heeftPlaatsbareBuit(player, ander)) {
        return nee('er valt niets te halen dat jij kwijt kunt');
      }
      return JA;
    }

    case 'ruil-bank': {
      if (state.phase !== 'hoofdfase') return nee('ruilen kan alleen in de hoofdfase');
      // Alles wat je weggeeft moet dezelfde soort zijn — dat is wat de koers betekent.
      const soorten = new Set(
        action.give.map((ref) => {
          const tile = player.principality.landscapes.find(
            (t) => t.column === ref.column && t.side === ref.side,
          );
          return tile === undefined ? '?' : LANDSCAPE_RESOURCE[tile.landscape];
        }),
      );
      if (soorten.size !== 1 || soorten.has('?')) {
        return nee('je ruilt met de bank telkens grondstoffen van één soort');
      }
      const soort = [...soorten][0] as Resource;
      const koers = bankRate(soort, tradeAbilitiesOf(player));
      if (action.give.length !== koers) {
        return nee(`de bank vraagt ${koers}× ${soort} voor één grondstof`);
      }
      const genoeg = checkPayment(player, { [soort]: koers } as never, action.give);
      if (!genoeg.ok) return nee(genoeg.reason);
      const doel = player.principality.landscapes.find(
        (t) => t.column === action.receive.column && t.side === action.receive.side,
      );
      if (doel === undefined) return nee('dat landschap bestaat niet');
      if (doel.resources >= LIMITS.resourcesPerLandscape) return nee('dat landschap is vol');
      return JA;
    }

    case 'ruil-aanbod': {
      if (state.phase !== 'hoofdfase') return nee('ruilen kan alleen in de hoofdfase');
      if (state.offer !== undefined) return nee('er ligt al een aanbod');
      if (state.offersThisTurn[seat] >= MAX_AANBIEDINGEN) {
        return nee(`je mag hoogstens ${MAX_AANBIEDINGEN} keer per beurt aanbieden`);
      }
      // Op "nee" volgt geen herhaling: wie opnieuw wil ruilen, komt met een ander voorstel.
      const alGeweigerd = state.afgewezenAanbiedingen.some(
        (eerder) =>
          eerder.from === seat &&
          zelfdeBundel(eerder.give, action.give) &&
          zelfdeBundel(eerder.want, action.want),
      );
      if (alGeweigerd) return nee('dat aanbod is al afgewezen');
      const zinnig = isSensibleOffer({ give: action.give, want: action.want });
      if (!zinnig.ok) return nee(zinnig.reason);
      if (!canOffer(player, { give: action.give, want: action.want })) {
        return nee('je kunt niet leveren wat je aanbiedt');
      }
      return JA;
    }

    case 'ruil-antwoord': {
      if (state.offer === undefined) return nee('er ligt geen aanbod');
      if (state.offer.from === seat) return nee('je kunt niet op je eigen aanbod antwoorden');
      if (
        action.accepteren &&
        !canFulfil(player, { give: state.offer.give, want: state.offer.want })
      ) {
        return nee('je kunt niet leveren wat er gevraagd wordt');
      }
      return JA;
    }

    case 'ruil-kaart': {
      if (state.phase !== 'aanvullen') return nee('kaarten ruilen kan aan het eind van je beurt');
      // "…mag hij één ontwikkelingskaart ruilen" (p. 10) — één, niet eindeloos rond.
      if (state.kaartGeruild === true) return nee('je hebt deze beurt al een kaart geruild');
      if (player.hand[action.handIndex] === undefined) return nee('die handkaart bestaat niet');
      const stapel = state.stacks[action.stack];
      if (stapel === undefined || stapel.length === 0) return nee('die stapel is leeg');
      return JA;
    }

    case 'ruil-af': {
      if (state.phase !== 'aanvullen') return nee('kaarten ruilen kan aan het eind van je beurt');
      // Zelfde grens als bij `ruil-kaart`: het boekje staat er per beurt precies één toe.
      if (state.kaartGeruild === true) return nee('je hebt deze beurt al een kaart geruild');
      if (player.hand[action.handIndex] === undefined) return nee('die handkaart bestaat niet');
      if (state.stacks[action.stack] === undefined) return nee('die stapel bestaat niet');
      // "Uitsluitend wanneer een speler zijn hand niet mag aanvullen" (handboek p. 10). Met
      // ruimte in je hand pak of koop je gewoon; ruilen is er voor wie vastzit.
      if (player.hand.length < handLimitOf(player)) {
        return nee('je hebt nog ruimte in je hand — pak of koop gewoon een kaart');
      }
      return JA;
    }

    case 'beurt-beeindigen':
      if (state.phase !== 'hoofdfase' && state.phase !== 'aanvullen') {
        return nee(`de beurt afsluiten kan niet in "${state.phase}"`);
      }
      return JA;
  }
}

function checkPaymentVerdict(
  player: PlayerState,
  kind: 'straat' | 'dorp' | 'stad',
  payment: Payment,
): Verdict {
  const cost = costOf(kind);
  if (!canAfford(player, cost)) return nee(`je hebt niet genoeg grondstoffen voor een ${kind}`);
  const check = checkPayment(player, cost, payment);
  return check.ok ? JA : nee(check.reason);
}

export interface Outcome {
  readonly state: GameState;
  readonly log: readonly string[];
}

/** Voert een actie uit. Roep eerst `isLegalAction` aan. */
/**
 * Voert een zet uit en bewaakt daarna de handlimiet.
 *
 * De limiet kan niet alleen stijgen maar ook dalen: een Vuurduivel die je stad met de
 * bibliotheek sloopt duwt die kaart terug in je hand én neemt de extra handplaats mee.
 * Het handboek lost dat op met msg 22 — het overtal gaat onder een stapel terug. Die
 * bewaking hoort op één plek, niet bij elke kaart die iets in een hand kan leggen.
 */
export function perform(state: GameState, seat: Seat, action: Action): Outcome {
  const uit = voerUit(state, seat, action);
  if (uit.state.pending !== undefined || uit.state.winner !== undefined) return uit;

  const teVol = uit.state.players.findIndex((p) => p.hand.length > handLimitOf(p));
  if (teVol < 0) return uit;
  return {
    state: { ...uit.state, pending: { kind: 'kies-afleggen', seat: teVol as Seat } },
    log: [...uit.log, `${uit.state.players[teVol]!.name} heeft te veel kaarten in de hand`],
  };
}

function voerUit(state: GameState, seat: Seat, action: Action): Outcome {
  const verdict = isLegalAction(state, seat, action);
  if (!verdict.ok) throw new Error(`onwettige zet: ${verdict.reason}`);

  switch (action.type) {
    case 'gooi': {
      const rolled = rollDice(state.rng);
      // De Alchimist bepaalt de opbrengst; de gebeurtenis blijft toeval.
      const worp =
        state.forcedProduction === undefined
          ? rolled.value
          : { ...rolled.value, production: state.forcedProduction };
      const na = resolveEvent(
        {
          ...state,
          rng: rolled.rng,
          dice: worp,
          phase: 'gebeurtenis',
          forcedProduction: undefined,
          // Een nieuwe worp wist de vorige gebeurteniskaart; anders blijft het scherm
          // hem tonen.
          lastEventCard: undefined,
        },
        CARD_STATS,
      );
      const log = [
        `${state.players[seat].name} gooit ${worp.production} en ${worp.event}`,
        ...(state.forcedProduction === undefined ? [] : ['(de Alchimist bepaalde de opbrengst)']),
        ...na.log,
      ];
      // Staat er nog een keuze open, dan wacht de opbrengst tot die gemaakt is.
      if (na.state.pending !== undefined) return { state: na.state, log };
      return finishEventPhase(na.state, log);
    }

    case 'tegenkaart': {
      const vraag = state.pending;
      if (vraag?.kind !== 'kies-tegenkaart') throw new Error('er staat geen tegenkaartvraag open');
      const naam = actionCardName(vraag.counterId);
      const log = action.inzetten
        ? [`${state.players[seat].name} zet de ${naam ?? 'tegenkaart'} in`]
        : [`${state.players[seat].name} laat de ${naam ?? 'tegenkaart'} liggen`];
      const na = beslechtAanval(
        { ...state, pending: undefined },
        vraag.attacker,
        vraag.attack as Parameters<typeof beslechtAanval>[2],
        vraag.target as CardTarget | undefined,
        action.inzetten,
        log,
      );
      // Een aanval kan bouwwerken doen verdwijnen; de figuren kunnen dus wisselen.
      const figuren = updateTokens(na.state);
      return {
        state: { ...na.state, knightToken: figuren.knight, windmillToken: figuren.windmill },
        log: [...na.log, ...figuren.log],
      };
    }

    case 'verkenner': {
      const vraag = state.pending;
      if (vraag?.kind !== 'verkenner-vraag') throw new Error('er staat geen verkennersvraag open');
      const players = [...state.players] as [PlayerState, PlayerState];
      const player = players[seat];
      const outward = groeirichting(player, vraag.column);
      const ruimte = Math.min(2, state.landscapeDeck.length);

      if (!action.inzetten) {
        // Geen Verkenner — dan gedekt trekken, maar de plaatsing blijft een keuze (p. 9).
        return {
          state: {
            ...state,
            landscapeDeck: state.landscapeDeck.slice(ruimte),
            pending:
              ruimte > 0
                ? {
                    kind: 'leg-landschap',
                    seat,
                    column: vraag.column + outward,
                    kaarten: state.landscapeDeck.slice(0, ruimte),
                  }
                : undefined,
          },
          log: [`${player.name} laat de Verkenner liggen`],
        };
      }

      // De kaart gaat eruit zodra je hem inzet, en dan mag je de stapel doorkijken.
      const kaartId = verkennerInHand(player);
      const hand = [...player.hand];
      if (kaartId !== undefined) hand.splice(hand.indexOf(kaartId), 1);
      players[seat] = { ...player, hand };
      return {
        state: {
          ...state,
          players,
          discard: kaartId === undefined ? state.discard : [...state.discard, kaartId],
          pending: {
            kind: 'verkenner-kies',
            seat,
            column: vraag.column,
            outward,
            zijde: 'boven',
            gekozen: [],
          },
        },
        log: [`${player.name} zet de Verkenner in en zoekt zelf twee landschappen uit`],
      };
    }

    case 'kies': {
      const na = resolveChoice(state, seat, { column: action.column, side: action.side });
      if (na.state.pending !== undefined) return na;
      // De gebeurtenis is rond; nu pas de opbrengst.
      if (na.state.phase === 'gebeurtenis') return finishEventPhase(na.state, na.log);
      return na;
    }

    case 'kies-klaar':
      // De Handelaar stopt na één grondstof; de teruggave volgt in `handelaarKlaar`.
      return handelaarKlaar(state, seat);

    case 'bouw-straat':
      return build(state, seat, 'straat', action.column, action.payment);
    case 'bouw-dorp':
      return build(state, seat, 'dorp', action.column, action.payment);
    case 'bouw-stad':
      return build(state, seat, 'stad', action.column, action.payment);

    case 'bouw-uitbreiding': {
      const kaart = cardById.get(action.cardId);
      const players = [...state.players] as [PlayerState, PlayerState];
      let speler = pay(players[seat], action.payment);

      // Kaart uit de hand naar het bouwwerk.
      const hand = [...speler.hand];
      hand.splice(hand.indexOf(action.cardId), 1);
      const buildings = speler.principality.buildings.map((b) =>
        b.column === action.column
          ? {
              ...b,
              expansions: {
                ...b.expansions,
                [action.side]: [...b.expansions[action.side], action.cardId],
              },
            }
          : b,
      );
      speler = { ...speler, hand, principality: { ...speler.principality, buildings } };
      players[seat] = speler;

      // Ridders en handelsgebouwen kunnen de speelfiguren doen wisselen.
      const tussen: GameState = { ...state, players };
      const figuren = updateTokens(tussen);
      const next: GameState = {
        ...tussen,
        knightToken: figuren.knight,
        windmillToken: figuren.windmill,
      };
      const log = [`${speler.name} bouwt ${kaart?.name ?? 'een uitbreiding'}`, ...figuren.log];

      const punten = victoryPoints(speler, CARD_POINTS, BUILDING_POINTS, {
        knight: next.knightToken,
        windmill: next.windmillToken,
      });
      if (punten >= LIMITS.victoryPoints) {
        log.push(`${speler.name} wint met ${punten} punten!`);
        return { state: { ...next, winner: seat, phase: 'afgelopen' }, log };
      }
      return { state: next, log };
    }

    case 'pak-kaart': {
      const stapel = state.stacks[action.stack] as readonly number[];
      const [bovenste, ...rest] = stapel;
      const stacks = state.stacks.map((s, i) => (i === action.stack ? rest : s));
      const players = [...state.players] as [PlayerState, PlayerState];
      players[seat] = { ...players[seat], hand: [...players[seat].hand, bovenste as number] };
      return {
        state: { ...state, stacks, players },
        log: [`${players[seat].name} pakt een kaart van stapel ${action.stack + 1}`],
      };
    }

    case 'speel-actiekaart': {
      const naam = actionCardName(action.cardId);
      if (naam === undefined) throw new Error('geen actiekaart');
      // De kaart gaat uit de hand naar de aflegstapel voordat hij werkt.
      const players = [...state.players] as [PlayerState, PlayerState];
      const hand = [...players[seat].hand];
      hand.splice(hand.indexOf(action.cardId), 1);
      players[seat] = { ...players[seat], hand };
      const zonderKaart: GameState = {
        ...state,
        players,
        discard: [...state.discard, action.cardId],
      };
      const gespeeld = playActionCard(zonderKaart, seat, naam, action.target);
      // Een aanval kan bouwwerken doen verdwijnen; de figuren kunnen dus wisselen.
      const figuren = updateTokens(gespeeld.state);
      return {
        state: {
          ...gespeeld.state,
          knightToken: figuren.knight,
          windmillToken: figuren.windmill,
        },
        log: [...gespeeld.log, ...figuren.log],
      };
    }

    case 'wissel-landschap': {
      const players = [...state.players] as [PlayerState, PlayerState];
      const tegels = [...players[seat].principality.landscapes];
      const iA = tegels.findIndex((t) => t.column === action.a.column && t.side === action.a.side);
      const iB = tegels.findIndex((t) => t.column === action.b.column && t.side === action.b.side);
      if (iA < 0 || iB < 0) throw new Error('dat landschap is niet van jou');
      // Alleen de soort en het getal verhuizen; de plek en wat erop ligt blijven.
      const a = tegels[iA]!;
      const b = tegels[iB]!;
      tegels[iA] = { ...a, landscape: b.landscape, dieNumber: b.dieNumber };
      tegels[iB] = { ...b, landscape: a.landscape, dieNumber: a.dieNumber };
      players[seat] = {
        ...players[seat],
        principality: { ...players[seat].principality, landscapes: tegels },
      };
      return { state: { ...state, players }, log: [] };
    }

    case 'plaats-landschap': {
      const players = [...state.players] as [PlayerState, PlayerState];
      const voorraad = [...state.setupPool[seat]];
      const [gekozen] = voorraad.splice(action.poolIndex, 1);
      if (gekozen === undefined) throw new Error('dat landschap heb je niet');
      players[seat] = {
        ...players[seat],
        principality: {
          ...players[seat].principality,
          landscapes: [
            ...players[seat].principality.landscapes,
            {
              landscape: gekozen.landscape,
              dieNumber: gekozen.dieNumber,
              column: action.column,
              side: action.side,
              // Elk landschap begint met één grondstof (handboek p. 10).
              resources: 1,
            },
          ],
        },
      };
      const pools: [readonly StartLandschap[], readonly StartLandschap[]] = [
        state.setupPool[0],
        state.setupPool[1],
      ];
      pools[seat] = voorraad;
      return {
        state: { ...state, players, setupPool: pools },
        log: [`${players[seat].name} legt een ${gekozen.landscape} neer`],
      };
    }

    case 'kies-startstapel':
      return {
        state: {
          ...state,
          pending: {
            kind: 'kies-startkaarten',
            seat,
            stack: action.stack,
            resterend: START_KAARTEN,
          },
        },
        log: [`${state.players[seat].name} kiest uit stapel ${action.stack + 1}`],
      };

    case 'opzet-klaar':
      return { state: { ...state, phase: 'dobbelen' }, log: ['het spel begint'] };

    case 'koop-kaart': {
      const players = [...state.players] as [PlayerState, PlayerState];
      players[seat] = pay(players[seat], action.payment);
      return {
        state: { ...state, players, pending: { kind: 'kies-uit-stapel', seat, stack: action.stack } },
        log: [
          `${players[seat].name} betaalt ${action.payment.length} grondstoffen om stapel ${action.stack + 1} door te kijken`,
        ],
      };
    }

    case 'ruil-bank': {
      const players = [...state.players] as [PlayerState, PlayerState];
      const betaald = pay(players[seat], action.give);
      const gekregen = addResource(betaald, action.receive.column, action.receive.side);
      players[seat] = gekregen.player;
      const tile = players[seat].principality.landscapes.find(
        (t) => t.column === action.receive.column && t.side === action.receive.side,
      );
      return {
        state: { ...state, players },
        log: [
          `${players[seat].name} ruilt ${action.give.length} grondstoffen bij de bank voor ${
            tile === undefined ? 'een grondstof' : LANDSCAPE_RESOURCE[tile.landscape]
          }`,
        ],
      };
    }

    case 'ruil-aanbod': {
      const geteld: [number, number] = [state.offersThisTurn[0], state.offersThisTurn[1]];
      geteld[seat] += 1;
      return {
        state: {
          ...state,
          offer: { from: seat, give: action.give, want: action.want },
          offersThisTurn: geteld,
        },
        log: [`${state.players[seat].name} doet een ruilaanbod`],
      };
    }

    case 'ruil-antwoord': {
      const offer = state.offer;
      if (offer === undefined) throw new Error('geen aanbod');
      if (!action.accepteren) {
        return {
          state: {
            ...state,
            offer: undefined,
            afgewezenAanbiedingen: [
              ...state.afgewezenAanbiedingen,
              { from: offer.from, give: offer.give, want: offer.want },
            ],
          },
          log: ['het aanbod is afgewezen'],
        };
      }
      const players = [...state.players] as [PlayerState, PlayerState];
      // De aanbieder geeft `give` en krijgt `want`; bij de ander andersom.
      const verplaats = (
        van: Seat,
        naar: Seat,
        bundel: Readonly<Partial<Record<Resource, number>>>,
      ): void => {
        for (const [resource, aantal] of Object.entries(bundel) as [Resource, number][]) {
          for (let i = 0; i < aantal; i++) {
            const bron = players[van].principality.landscapes.find(
              (t) => LANDSCAPE_RESOURCE[t.landscape] === resource && t.resources > 0,
            );
            if (bron === undefined) continue;
            players[van] = removeResource(players[van], bron.column, bron.side).player;
            const doel =
              players[naar].principality.landscapes.find(
                (t) => LANDSCAPE_RESOURCE[t.landscape] === resource && t.resources < 3,
              ) ?? players[naar].principality.landscapes[0];
            if (doel === undefined) continue;
            players[naar] = addResource(players[naar], doel.column, doel.side).player;
          }
        }
      };
      verplaats(offer.from, seat, offer.give);
      verplaats(seat, offer.from, offer.want);
      return {
        state: { ...state, players, offer: undefined },
        log: [`${players[seat].name} gaat akkoord met de ruil`],
      };
    }

    case 'ruil-kaart': {
      const players = [...state.players] as [PlayerState, PlayerState];
      const hand = [...players[seat].hand];
      const [weg] = hand.splice(action.handIndex, 1);
      const stapel = state.stacks[action.stack] as readonly number[];
      const [bovenste, ...rest] = stapel;
      hand.push(bovenste as number);
      players[seat] = { ...players[seat], hand };
      // De afgelegde kaart gaat onderop diezelfde stapel.
      const stacks = state.stacks.map((s2, i) =>
        i === action.stack ? [...rest, weg as number] : s2,
      );
      return {
        state: { ...state, players, stacks, kaartGeruild: true },
        log: [`${players[seat].name} ruilt een handkaart`],
      };
    }

    case 'ruil-af': {
      // Alleen wégleggen; wat je ervoor terugkrijgt bepaal je daarna zelf met `pak-kaart`
      // of `koop-kaart`. De kaart gaat ondérop, zoals het handboek voorschrijft — bovenop
      // zou hem de volgende trekbeurt zo weer teruggeven.
      const players = [...state.players] as [PlayerState, PlayerState];
      const hand = [...players[seat].hand];
      const [weg] = hand.splice(action.handIndex, 1);
      players[seat] = { ...players[seat], hand };
      const stacks = state.stacks.map((s2, i) =>
        i === action.stack ? [...s2, weg as number] : s2,
      );
      return {
        state: { ...state, players, stacks, kaartGeruild: true },
        log: [`${players[seat].name} legt een handkaart onder een stapel`],
      };
    }

    case 'beurt-beeindigen': {
      // Aan het eind van je beurt komt de kaartvraag (handboek p. 16). Die komt **altijd**
      // zolang er nog kaarten liggen: heb je ruimte, dan mag je pakken of kopen; zit je
      // vol, dan mag je er nog één ruilen. Eerder sloeg het spel die vraag over bij een
      // volle hand, en dan kon je nooit meer ruilen.
      if (state.phase === 'hoofdfase' && state.stacks.some((stapel) => stapel.length > 0)) {
        return {
          state: { ...state, phase: 'aanvullen' },
          log: ['vul je hand aan, ruil een kaart, of sluit je beurt af'],
        };
      }
      const next: Seat = seat === 0 ? 1 : 0;
      return {
        state: {
          ...state,
          turn: next,
          turnNumber: state.turnNumber + 1,
          phase: 'dobbelen',
          dice: undefined,
          offer: undefined,
          // Nieuwe beurt, nieuwe kans om te ruilen — ook met een eerder afgewezen voorstel.
          offersThisTurn: [0, 0],
          afgewezenAanbiedingen: [],
          kaartGeruild: false,
        },
        log: [`${state.players[next].name} is aan de beurt`],
      };
    }
  }
}

/** Deelt de opbrengst uit en zet de beurt in de hoofdfase. */
function finishEventPhase(state: GameState, log: readonly string[]): Outcome {
  const roll = state.dice?.production;
  if (roll === undefined) throw new Error('geen worp om uit te delen');
  const produced = distributeProduction(state, roll);
  return { state: produced.state, log: [...log] };
}

/**
 * Trekt twee nieuwe landschappen bij een nieuw dorp.
 *
 * Fysieke regels 1996: "When placing a Settlement, draw 2 Region cards and place them
 * at the outside corners of the new Settlement, showing 0 resources." De buitenhoek is
 * de even kolom aan de buitenkant; de andere kant deelt het dorp met zijn buurman.
 */
function build(
  state: GameState,
  seat: Seat,
  kind: 'straat' | 'dorp' | 'stad',
  column: number,
  payment: Payment,
): Outcome {
  const players = [...state.players] as [PlayerState, PlayerState];
  let player = pay(players[seat], payment);
  const rng = state.rng;
  const p = player.principality;
  let stapel = state.landscapeDeck;
  /** Staat er nog een vraag open na dit bouwwerk? */
  let vraag: GameState['pending'];

  if (kind === 'straat') {
    player = { ...player, principality: { ...p, roads: [...p.roads, { column }] } };
  } else if (kind === 'dorp') {
    player = {
      ...player,
      principality: {
        ...p,
        buildings: [...p.buildings, { kind: 'dorp', column, expansions: { boven: [], onder: [] } }],
      },
    };
    const outward = groeirichting(player, column);
    const ruimte = Math.min(2, stapel.length);

    // Heb je de Verkenner op zak, dan vraagt het spel of je hem inzet — dát is het moment
    // waarop die kaart hoort te komen. Anders komen de landen gedekt van de stapel en
    // beslist de speler zelf welk land boven en welk onder komt (p. 9): de vraag
    // `leg-landschap` draagt de getrokken kaarten.
    if (ruimte > 0 && verkennerInHand(player) !== undefined) {
      vraag = { kind: 'verkenner-vraag', seat, column };
    } else if (ruimte > 0) {
      vraag = { kind: 'leg-landschap', seat, column: column + outward, kaarten: stapel.slice(0, ruimte) };
      stapel = stapel.slice(ruimte);
    }
  } else {
    player = {
      ...player,
      principality: {
        ...p,
        buildings: p.buildings.map((b) => (b.column === column ? { ...b, kind: 'stad' as const } : b)),
      },
    };
  }
  players[seat] = player;

  // De speelfiguren kunnen door deze zet van eigenaar wisselen; ze tellen mee voor de
  // winst, dus ze mogen geen beurt achterlopen.
  const tussen: GameState = { ...state, players, rng, landscapeDeck: stapel, pending: vraag };
  const figuren = updateTokens(tussen);
  const next: GameState = {
    ...tussen,
    knightToken: figuren.knight,
    windmillToken: figuren.windmill,
  };
  const punten = victoryPoints(player, CARD_POINTS, BUILDING_POINTS, {
    knight: next.knightToken,
    windmill: next.windmillToken,
  });
  const log = [`${player.name} bouwt een ${kind}`, ...figuren.log];

  if (punten >= LIMITS.victoryPoints) {
    log.push(`${player.name} wint met ${punten} punten!`);
    // Wint dit dorp het potje, dan is er niets meer te verkennen: de vraag zou blijven
    // openstaan met niemand om hem te beantwoorden. De landschappen komen dan gewoon van
    // de stapel, zodat het eindbeeld klopt.
    if (vraag !== undefined) {
      // Bij `leg-landschap` liggen de getrokken kaarten al in de vraag; bij de
      // verkennersvraag nog op de stapel.
      const vanVraag = vraag.kind === 'leg-landschap' ? vraag.kaarten : undefined;
      const ruimte = vanVraag === undefined ? Math.min(2, stapel.length) : 0;
      players[seat] = legLandschappen(
        player,
        column + groeirichting(player, column),
        vanVraag ?? stapel.slice(0, ruimte),
      );
      return {
        state: {
          ...next,
          players,
          landscapeDeck: stapel.slice(ruimte),
          pending: undefined,
          winner: seat,
          phase: 'afgelopen',
        },
        log,
      };
    }
    return { state: { ...next, winner: seat, phase: 'afgelopen' }, log };
  }
  // Bewust zonder de kaart te noemen: dit verslag gaat naar béíde spelers, en "kan de
  // Verkenner inzetten" zou verklappen dat hij hem in de hand heeft — ook als hij zo
  // meteen nee zegt. Pas bij het inzetten zelf wordt de kaart openbaar.
  if (vraag !== undefined) log.push(`${player.name} richt zijn nieuwe dorp in`);
  // De stapel telt elf landen voor het hele potje (p. 16). Raakt hij op, dan hoort dat
  // gezegd — een dorp met één land of geen landen oogt anders als een fout.
  if (kind === 'dorp' && (vraag === undefined || vraag.kind === 'leg-landschap')) {
    const gelegd = vraag?.kind === 'leg-landschap' ? vraag.kaarten.length : 0;
    if (gelegd === 1) log.push('de landenstapel is bijna op — dit dorp krijgt nog één land');
    if (gelegd === 0 && stapel.length === 0) {
      log.push('de landenstapel is op — dit dorp krijgt geen landen meer');
    }
  }
  return { state: next, log };
}

/**
 * De score van een solopotje (handboek p. 8-9).
 *
 * `100 + (verschil in overwinningspunten × 100) + (verschil in gekregen grondstoffen)`,
 * waarbij alleen grondstoffen uit de **opbrengstendobbelsteen** meetellen — wat je
 * ruilt of met actiekaarten pakt niet.
 */
export function soloScore(state: GameState, human: Seat): number {
  const computer: Seat = human === 0 ? 1 : 0;
  const vo = pointsOf(state, human) - pointsOf(state, computer);
  const vg = state.produced[computer] - state.produced[human];
  return 100 + vo * 100 + vg;
}

/** Overwinningspunten van een speler, met de kaartdata erbij. */
export function pointsOf(state: GameState, seat: Seat): number {
  return victoryPoints(state.players[seat], CARD_POINTS, BUILDING_POINTS, {
    knight: state.knightToken,
    windmill: state.windmillToken,
  });
}
